import { getFunctions, httpsCallable } from "firebase/functions";
import firebaseApp, { db } from "services/firebase";

/**
 * Patient-uploaded medical documents (/dashboard/masterchart).
 *
 * Every upload goes inline as base64 — the 5MB cap sits comfortably under the
 * 8MB callable limit, so unlike embeddedDocumentsService there is no Storage
 * staging path to manage. The callable extracts flat text at ingest and that
 * text is what reaches the chat prompt.
 */

/** Hard cap per file, mirrored by the callable and firebase/storage.rules. */
export const MAX_FILE_BYTES = 5 * 1024 * 1024;

/** Extensions the server-side extractor supports. */
export const ACCEPTED_EXTENSIONS = [
  ".pdf",
  ".doc",
  ".docx",
  ".odt",
  ".odf",
  ".ppt",
  ".pptx",
  ".txt",
  ".md",
];

const functions = getFunctions();

/**
 * Last list result, so returning to the documents page renders at once instead
 * of calling a function that may be cold. Keyed by uid so a different sign-in
 * never sees someone else's list. Every mutation here keeps it current.
 */
let listCache = null;
/** The list call in flight, so a prefetch and an open share one request. */
let listInFlight = null;

function currentUid() {
  return firebaseApp.auth().currentUser?.uid ?? null;
}

/** The cached list for the signed-in user, or null if there isn't one. */
export function getCachedDocuments() {
  const uid = currentUid();
  return listCache && uid && listCache.uid === uid ? listCache.result : null;
}

function invalidateList() {
  listCache = null;
  listInFlight = null;
}

const uploadCallable = httpsCallable(functions, "patientdocuments_upload", {
  timeout: 300000,
});
const listCallable = httpsCallable(functions, "patientdocuments_list", {
  timeout: 60000,
});
const deleteCallable = httpsCallable(functions, "patientdocuments_delete", {
  timeout: 60000,
});
const setIncludeInChatCallable = httpsCallable(
  functions,
  "patientdocuments_setincludeinchat",
  { timeout: 60000 }
);
// Fans out several LLM calls per document, so it needs the same long timeout
// the callable itself runs with.
const checkConsistencyCallable = httpsCallable(
  functions,
  "patientdocuments_checkconsistency",
  { timeout: 540000 }
);

/** Convert a File to base64, data-URI prefix stripped. */
function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      if (reader.error) {
        reject(reader.error);
        return;
      }
      resolve(String(reader.result).split(",")[1]);
    };
    reader.onerror = () =>
      reject(reader.error || new Error("Failed to read file."));
    reader.readAsDataURL(file);
  });
}

/**
 * Upload one document. Rejects oversized files before spending time on the
 * base64 read, so the user gets the size error immediately.
 * @param {File} file
 * @returns {Promise<Object>} { docId, fileName, characterCount, truncated }
 */
export async function uploadDocument(file) {
  if (file.size > MAX_FILE_BYTES) {
    throw new Error(
      `"${file.name}" is ${(file.size / (1024 * 1024)).toFixed(1)}MB. Documents must be 5MB or smaller.`
    );
  }

  const fileBase64 = await fileToBase64(file);
  const result = await uploadCallable({
    fileBase64,
    fileName: file.name,
    fileType: file.type || "application/octet-stream",
  });
  invalidateList();
  return result.data;
}

/**
 * List the caller's documents, newest first.
 * @returns {Promise<{documents: Array, maxDocuments: number}>}
 */
export async function listDocuments({ force = false } = {}) {
  const cached = getCachedDocuments();
  if (cached && !force) return cached;

  if (listInFlight && !force) return listInFlight;

  const uid = currentUid();
  const request = listCallable({})
    .then((response) => {
      const result = {
        documents: response.data?.documents ?? [],
        maxDocuments: response.data?.maxDocuments ?? 0,
      };
      // A mutation mid-request invalidates; don't cache what it superseded.
      if (uid && listInFlight === request) listCache = { uid, result };
      return result;
    })
    .finally(() => {
      if (listInFlight === request) listInFlight = null;
    });
  listInFlight = request;
  return request;
}

/**
 * Permanently delete a document and its stored file.
 * @param {string} docId
 */
export async function deleteDocument(docId) {
  const result = await deleteCallable({ docId });
  invalidateList();
  return result.data;
}

/**
 * Toggle whether a document's text is fed into chat conversations. Keeps the
 * document on file either way.
 * @param {string} docId
 * @param {boolean} includeInChat
 */
export async function setIncludeInChat(docId, includeInChat) {
  const result = await setIncludeInChatCallable({ docId, includeInChat });
  // Patch rather than drop: the page already shows the new value, so there is
  // nothing to refetch.
  if (listCache) {
    listCache = {
      ...listCache,
      result: {
        ...listCache.result,
        documents: listCache.result.documents.map((d) =>
          d.id === docId ? { ...d, includeInChat } : d
        ),
      },
    };
  }
  return result.data;
}

/**
 * Cross-check every stored document for discrepancies: a document belonging to
 * someone else, values impossible as written, and statements that contradict
 * each other. Reads the stored text server-side — nothing is re-uploaded.
 *
 * @returns {Promise<{
 *   issues: Array<{
 *     check: string,
 *     severity: "high"|"medium"|"low"|"info",
 *     summary: string,
 *     docIds: string[],
 *     evidence: string[],
 *     confidence: number|null,
 *     suggestedAction: string|null,
 *   }>,
 *   documentCount: number,
 *   truncatedDocuments: string[],
 *   errors: string[],
 * }>}
 */
export async function checkConsistency({ checks } = {}) {
  const result = await checkConsistencyCallable(checks ? { checks } : {});
  return {
    issues: result.data?.issues ?? [],
    // Maps each issue's docIds label back to the Firestore id and file name.
    documents: result.data?.documents ?? [],
    documentCount: result.data?.documentCount ?? 0,
    truncatedDocuments: result.data?.truncatedDocuments ?? [],
    errors: result.data?.errors ?? [],
  };
}

/**
 * Subscribe to the caller's most recent consistency check.
 *
 * The callable cannot stream, so it writes progress to this document as it
 * runs. Subscribing rather than awaiting the callable means a page reload
 * mid-run reattaches to the run in flight, and a completed run stays visible
 * across reloads until the next check replaces it.
 *
 * @param {(state: object|null) => void} onChange receives null when no check
 *   has ever run
 * @param {(error: Error) => void} [onError]
 * @returns {() => void} unsubscribe
 */
export function subscribeToLatestCheck(onChange, onError) {
  const uid = firebaseApp.auth().currentUser?.uid;
  if (!uid) {
    onChange(null);
    return () => {};
  }

  return db
    .collection("users")
    .doc(uid)
    .collection("documentChecks")
    .doc("latest")
    .onSnapshot(
      (snap) => {
        if (!snap.exists) {
          onChange(null);
          return;
        }
        const data = snap.data() || {};
        onChange({
          status: data.status || "completed",
          progress: data.progress || null,
          issues: data.issues ?? [],
          documentCount: data.documentCount ?? 0,
          documentIds: data.documentIds ?? [],
          documents: data.documents ?? [],
          startedAt: data.startedAt?.toMillis?.() ?? null,
          truncatedDocuments: data.truncatedDocuments ?? [],
          errors: data.errors ?? [],
          error: data.error ?? null,
          completedAt: data.completedAt?.toMillis?.() ?? null,
        });
      },
      (err) => {
        if (onError) onError(err);
      }
    );
}

/**
 * A check that finished on the server after `sinceMs`, waiting for one still
 * running if it started recently enough to plausibly finish. Lets a resumed
 * run pick up the result of a check the browser stopped listening to, rather
 * than paying for the same check twice. Resolves null when there is nothing
 * usable, and the caller runs a fresh check.
 *
 * @param {number} sinceMs
 * @returns {Promise<Object|null>} the same shape subscribeToLatestCheck emits
 */
export function getSettledLatestCheck(sinceMs) {
  // The callable's own timeout; a "running" run older than this is dead.
  const MAX_RUN_MS = 540000;
  return new Promise((resolve) => {
    let unsubscribe = () => {};
    let timer = null;
    let done = false;
    const finish = (value) => {
      if (done) return;
      done = true;
      clearTimeout(timer);
      unsubscribe();
      resolve(value);
    };
    unsubscribe = subscribeToLatestCheck(
      (state) => {
        if (!state || !state.startedAt || state.startedAt < sinceMs) {
          finish(null);
        } else if (state.status === "running") {
          const left = state.startedAt + MAX_RUN_MS - Date.now();
          if (left <= 0) finish(null);
          // A run that dies without writing "failed" never fires again, so
          // stop waiting once it is past its timeout.
          else if (!timer) timer = setTimeout(() => finish(null), left);
        } else {
          finish(state.status === "completed" ? state : null);
        }
      },
      () => finish(null)
    );
    if (done) unsubscribe();
  });
}
